import {
  Box,
  Button,
  HStack,
  Heading,
  Popover,
  Portal,
  Spinner,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import {
  useBlocksConfig,
  useEditorStateSelector
} from "@pathwright/blocks-engine"
import {
  CautionTriangleIcon,
  CheckIcon,
  ChevronDownIcon
} from "@pathwright/pathicons"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import pluralize from "pluralize"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import ContentPublishDiff from "./ContentPublishDiff"

const Indicator = ({ needsPublish }: { needsPublish?: boolean }) => {
  return needsPublish ? (
    <Box
      borderRadius="50%"
      height="12px"
      width="12px"
      bg="red.500"
      position="absolute"
      right="-2px"
      top="-2px"
    />
  ) : null
}

type ContentPublishActionProps = {
  needsPublish: boolean
}

const ContentPublishAction = ({ needsPublish }: ContentPublishActionProps) => {
  const { open, onToggle, onClose } = useDisclosure()
  const { tc } = useTranslate()
  const syncing = useEditorStateSelector((state) => state.syncing)
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // For some reason, non-user blur events are being triggered,
  // so we need to add our own handler for toggling the popover.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node | null) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node | null)
      ) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose])

  return (
    <HStack>
      <Popover.Root
        open={open}
        closeOnInteractOutside={false}
        lazyMount
        unmountOnExit={false}
      >
        <Popover.Trigger asChild>
          <Button
            ref={triggerRef}
            onClick={onToggle}
            variant="outline"
            colorPalette="gray"
            borderRadius="50px"
            borderColor="gray.400"
            bg="blackAlpha.50"
            _hover={{
              bg: "blackAlpha.100"
            }}
            px={4}
            fontWeight="600"
            pos="relative"
            lineHeight="1"
            h={9}
            cursor="pointer"
            opacity={needsPublish ? 1 : 0.4}
            disabled={syncing}
          >
            {!needsPublish && (
              <CheckIcon size="1em" color="var(--chakra-colors-gray-600)" />
            )}
            {needsPublish ? tc("Save") : tc("Saved")}
            {syncing ? (
              <Spinner size="sm" color="gray.600" />
            ) : (
              <ChevronDownIcon
                size="1em"
                color="var(--chakra-colors-gray-600)"
              />
            )}
            <Indicator needsPublish={needsPublish} />
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <ContentPublishPopoverBody
              needsPublish={needsPublish}
              popoverRef={popoverRef}
              onClose={onClose}
            />
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </HStack>
  )
}

type ContentPublishPopoverBodyProps = {
  needsPublish: boolean
  popoverRef: React.RefObject<HTMLDivElement>
  onClose: () => void
}

const ContentPublishPopoverBody = ({
  needsPublish,
  popoverRef,
  onClose
}: ContentPublishPopoverBodyProps) => {
  const { saveAudience } = useBlocksConfig()
  const { t, tc, tca } = useTranslate()
  const [discardingDraft, setDiscardingDraft] = useState(false)

  const changes = useEditorStateSelector((state) => state.changes)
  const handleDiscardDraftContent = useEditorStateSelector(
    (state) => state.handleDiscardDraftContent
  )
  const handlePublishContent = useEditorStateSelector(
    (state) => state.handlePublishContent
  )

  return (
    <Popover.Content
      ref={popoverRef}
      css={{
        "& p": { marginTop: 0, marginBottom: 0 }
      }}
    >
      <Popover.Arrow>
        <Popover.ArrowTip borderColor="gray.200" />
      </Popover.Arrow>
      <Popover.Body
        p={4}
        maxH={`calc(100vh - 220px)`}
        overflowY="auto"
        overflowX="hidden"
      >
        <VStack
          textAlign="center"
          minWidth="250px"
          width="full"
          gap={"16px"}
          css={{
            "& p:last-of-type": {
              paddingTop: 0
            }
          }}
          borderRadius="10px"
        >
          <VStack justify="center" w="full" gap={1}>
            <Pathicon
              aria-label="block-icon"
              icon={needsPublish ? "block-notification-fill" : "block-fill"}
              display="flex"
              alignItems="center"
              height="27px"
              width="27px"
              p={0}
              m="0 10px"
              color="rgb(170, 170, 170)"
              fontSize="1.2em"
              cursor={"default"}
              bg="transparent"
              css={{
                ["& svg"]: {
                  height: "27px",
                  width: "27px"
                }
              }}
              _hover={undefined}
              _active={{
                bg: "transparent"
              }}
            />
            <Heading
              as="h3"
              size="md"
              textAlign="center"
              fontWeight={700}
              my={0}
              pos="relative"
            >
              <HStack gap={0}>
                <Text as="span" fontWeight={700}>
                  {needsPublish ? (
                    <>
                      {changes?.blocks?.length || ""}{" "}
                      {tca(pluralize("Change", changes?.blocks?.length))}
                    </>
                  ) : (
                    tc("All changes saved")
                  )}
                </Text>
                <Box pos="relative" transform="translateY(-50%)">
                  <Tooltip
                    title={
                      <Text
                        fontSize="sm"
                        textAlign="left"
                        color="white"
                        lineHeight="1.4"
                      >
                        {tc(
                          `Blocks changes don’t require Publishing with the Path and are available to everyone as soon as you save them.`
                        )}{" "}
                        <Link
                          variant="underline"
                          href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content"
                          color="inherit"
                          textDecorationColor="inherit"
                        >
                          {tc("Learn more about saving Blocks")}
                        </Link>
                        .
                      </Text>
                    }
                  >
                    <Link
                      target="_blank"
                      href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content"
                    >
                      <Pathicon
                        icon="info-circle"
                        size=".85em"
                        color="PRIMARY_GRAY"
                      />
                    </Link>
                  </Tooltip>
                </Box>
              </HStack>
            </Heading>
          </VStack>

          {needsPublish ? (
            <Text textAlign="center">
              {tc("Save changes to share them with everyone")}
              {saveAudience?.name && (
                <Box as="span">
                  {" "}
                  {t("in")} <strong>{saveAudience.name}</strong>
                </Box>
              )}
              .
            </Text>
          ) : (
            <Text textAlign="center">
              {saveAudience?.savedDescription ||
                tc("All Blocks in this Step are shared with with everyone")}
              {saveAudience?.name ? (
                <Box as="span">
                  {" "}
                  {t("in")} <strong>{saveAudience.name}</strong>
                </Box>
              ) : (
                ""
              )}
              .
            </Text>
          )}
          <ContentPublishDiff
            onClickBlockChange={(blockId) => {
              // smoothly navigate to #<blockId>
              const block = document.getElementById(`block-${blockId}`)
              if (block) {
                block.scrollIntoView({
                  behavior: "smooth",
                  block: "center"
                })
              }
              onClose()
            }}
            changes={changes}
          />
          {needsPublish ? (
            <>
              {saveAudience?.warning ? (
                <HStack maxW="250px" margin="20px auto" align="center" gap={3}>
                  <CautionTriangleIcon
                    color="var(--chakra-colors-gray-700)"
                    size="1.4em"
                  />
                  <Text>{saveAudience.warning}</Text>
                </HStack>
              ) : null}
              <VStack my={2} gap={0} maxW="100%">
                {discardingDraft ? (
                  <>
                    <Text textAlign="center">
                      {tc(
                        "This will remove all unsaved changes. Are you sure?"
                      )}
                    </Text>
                    <Button
                      my={2}
                      colorPalette="red"
                      onClick={() => {
                        setDiscardingDraft(false)
                        onClose()
                        handleDiscardDraftContent!()
                      }}
                      cursor="pointer"
                    >
                      {tc("Yes, discard changes")}
                    </Button>
                    <Button
                      mb={2}
                      colorPalette="gray"
                      variant="subtle"
                      onClick={() => setDiscardingDraft(false)}
                      cursor="pointer"
                    >
                      {tc("No, cancel")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      mb={2}
                      disabled={!needsPublish}
                      colorPalette="brand"
                      onClick={() => {
                        onClose()
                        handlePublishContent!()
                      }}
                      cursor="pointer"
                      display="block"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                    >
                      {saveAudience?.label || tc("Save Blocks")}
                    </Button>
                    <Button
                      variant="plain"
                      textDecoration="underline"
                      colorPalette="gray"
                      color="PRIMARY_GRAY"
                      bg="transparent"
                      _hover={{
                        bg: "transparent"
                      }}
                      onClick={() => setDiscardingDraft(true)}
                      cursor="pointer"
                    >
                      {tc("Or revert changes")}
                    </Button>
                  </>
                )}
              </VStack>
            </>
          ) : null}
        </VStack>
      </Popover.Body>
    </Popover.Content>
  )
}

export default ContentPublishAction
